import { CloudUpload, File, FileText, FolderOpen, Image as ImageIcon, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { queryPersistMeta } from '@/lib/pwa/queryPersistence';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
  api,
  fetchEscritorios,
  uploadArtefatoInit,
  type EscritorioResumo,
} from '@/api/client';
import { ClienteCombobox } from '@/components/ClienteCombobox';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import type { TagResumo } from '@/api/tags';
import { TagSeletor } from '@/components/tags/TagSeletor';
import { useNotification } from '@/context/NotificationProvider';
import { formatFileSize } from '@/lib/formatters';
import { queryKeys } from '@/lib/pwa/queryConfig';
import { reportClassifiedError } from '@/lib/observability/sentry';
import { sha256OfBlob, uploadBlobToS3 } from '@/lib/upload';

function FileTypeIcon({ mime }: { mime: string }) {
  const Icon = mime.startsWith('image/') ? ImageIcon
    : mime === 'application/pdf' ? FileText
    : mime.includes('word') || mime.includes('document') ? FileText
    : FolderOpen;
  return (
    <div className="flex h-12 w-12 shrink-0 flex-col items-center justify-center rounded-lg bg-muted">
      <Icon className="h-6 w-6 text-muted-foreground" />
    </div>
  );
}

interface UploadDocumentoDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** ID do cliente pré-selecionado (fixo, sem possibilidade de alterar) */
  fixedClienteId?: string;
  /** Nome do cliente pré-selecionado (para exibição) */
  fixedClienteNome?: string;
  /** ID do escritório pré-selecionado (fixo, oculta seletor) */
  fixedEscritorioId?: string;
  /** Query keys adicionais para invalidar após upload */
  extraInvalidateKeys?: readonly (readonly string[])[];
}

export function UploadDocumentoDialog({
  open,
  onOpenChange,
  fixedClienteId,
  fixedClienteNome,
  fixedEscritorioId,
  extraInvalidateKeys = [],
}: UploadDocumentoDialogProps) {
  const queryClient = useQueryClient();
  const { showSuccess, showWarning } = useNotification();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [descricao, setDescricao] = useState('');
  const [tags, setTags] = useState<TagResumo[]>([]);
  const [escritorioId, setEscritorioId] = useState(fixedEscritorioId ?? '');
  const [clienteId, setClienteId] = useState<string | null>(fixedClienteId ?? null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const { data: escritorios } = useQuery<EscritorioResumo[]>({
    queryKey: ['escritorios'],
    queryFn: fetchEscritorios,
    meta: queryPersistMeta.nonEmpty,
  });
  const hasEscritorios = escritorios && escritorios.length > 0;

  function resetFields() {
    setUploadFile(null);
    setDescricao('');
    setTags([]);
    setEscritorioId(fixedEscritorioId ?? '');
    setClienteId(fixedClienteId ?? null);
    setError('');
  }

  function close() {
    resetFields();
    onOpenChange(false);
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setError('Arquivo excede o limite de 10MB');
      return;
    }
    setUploadFile(file);
    setError('');
  }

  async function handleUpload() {
    if (!uploadFile) return;
    setUploading(true);
    setError('');

    try {
      const hash = await sha256OfBlob(uploadFile);
      const initResponse = await uploadArtefatoInit({
        nome: uploadFile.name,
        mime: uploadFile.type || 'application/octet-stream',
        tamanho: uploadFile.size,
        hash,
        tipo: uploadFile.type.startsWith('image/') ? 'IMAGEM'
          : uploadFile.type.startsWith('audio/') ? 'AUDIO'
          : uploadFile.type.startsWith('video/') ? 'VIDEO'
          : 'DOCUMENTO',
        descricao: descricao || undefined,
        // Pelos ids: o nome é ambíguo quando a tag pessoal e a do escritório se chamam
        // igual. (O `tags` por nome segue aceito só para o PWA com cache antigo.)
        tag_ids: tags.length > 0 ? tags.map((t) => t.id) : undefined,
        escritorio_id: escritorioId || undefined,
        cliente_id: (fixedClienteId || clienteId) || undefined,
      });

      if (initResponse.is_duplicate) {
        // If uploading from dossiê (fixedClienteId), relink existing artefato to this client
        if (fixedClienteId && initResponse.artefato_id) {
          try {
            await api.put(`/artefatos/${initResponse.artefato_id}`, {
              cliente_id: fixedClienteId,
            });
          } catch {
            // Best effort — artefato already exists, linking failed
          }
        }
        queryClient.invalidateQueries({ queryKey: [...queryKeys.documentos.artefatos] });
        for (const key of extraInvalidateKeys) {
          queryClient.invalidateQueries({ queryKey: [...key] });
        }
        showWarning('Este arquivo já existe na sua biblioteca');
        close();
        return;
      }

      await uploadBlobToS3(
        { upload_url: initResponse.upload_url, fields: initResponse.fields },
        uploadFile,
        uploadFile.name,
      );

      queryClient.invalidateQueries({ queryKey: [...queryKeys.documentos.artefatos] });
      for (const key of extraInvalidateKeys) {
        queryClient.invalidateQueries({ queryKey: [...key] });
      }
      showSuccess('Documento enviado com sucesso');
      close();
    } catch (err: unknown) {
      // O POST presigned ao S3 é um XMLHttpRequest cru (fora do axios
      // instrumentado), então uma falha aqui NÃO seria capturada pelo Sentry
      // automaticamente — historicamente ela só virava um <Alert> inline e
      // ficava invisível no monitoramento. Reportamos explicitamente: a política
      // central (SENTRY_POLICY) decide capture vs breadcrumb, então erros de
      // validação do upload-init viram breadcrumb e falhas de rede/S3 viram issue.
      // Sem o nome do arquivo nas tags/contexto (pode conter dados do cliente).
      reportClassifiedError(err, undefined, {
        tags: { operation: 'artefato_upload' },
        context: {
          name: 'upload',
          data: {
            mime: uploadFile.type || 'application/octet-stream',
            tamanho: uploadFile.size,
            destino: escritorioId ? 'compartilhado' : 'pessoal',
          },
        },
      });
      const axiosErr = err as { response?: { status?: number; data?: { detail?: string } }; message?: string };
      const status = axiosErr?.response?.status;
      const detail = axiosErr?.response?.data?.detail;
      const msg = axiosErr?.message || String(err);
      const isFetchError = msg.includes('Failed to fetch') || msg.includes('NetworkError');

      if (isFetchError) {
        setError('Erro de conexão ao enviar o arquivo. Verifique se o serviço de armazenamento está disponível.');
      } else if (status === 500) {
        setError('Erro interno do servidor ao processar o upload. O serviço de armazenamento pode estar indisponível.');
      } else if (detail) {
        setError(detail);
      } else {
        setError(msg || 'Erro ao fazer upload');
      }
    } finally {
      setUploading(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) resetFields(); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Upload de Documento</DialogTitle>
          <DialogDescription>
            {fixedClienteNome
              ? `Enviar documento para o cliente ${fixedClienteNome}.`
              : 'Selecione um arquivo para enviar ao seu escritório.'}
          </DialogDescription>
        </DialogHeader>

        <input
          ref={fileInputRef}
          type="file"
          hidden
          onChange={handleFileSelect}
          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp"
        />

        <div
          onClick={() => fileInputRef.current?.click()}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            uploadFile
              ? 'border-primary bg-primary/5'
              : 'border-border hover:border-primary hover:bg-primary/5'
          }`}
        >
          {uploadFile ? (
            <div className="flex w-full flex-col items-center gap-2">
              <FileTypeIcon mime={uploadFile.type} />
              <p className="w-full [overflow-wrap:anywhere] text-center font-medium">
                {uploadFile.name}
              </p>
              <p className="text-sm text-muted-foreground">
                {formatFileSize(uploadFile.size)}
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <CloudUpload className="h-12 w-12 text-muted-foreground" />
              <p>Clique para selecionar um arquivo</p>
              <p className="text-sm text-muted-foreground">
                PDF, DOC, DOCX, JPG, PNG (max. 10MB)
              </p>
            </div>
          )}
        </div>

        {error && (
          <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Descrição</Label>
            <Textarea
              placeholder="Descrição do documento (opcional)"
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              rows={2}
            />
          </div>

          <div className="space-y-1.5">
            <Label>Tags</Label>
            <TagSeletor value={tags} onChange={setTags} identificador={uploadFile?.name ?? 'novo documento'} />
          </div>

          {/* Cliente: fixo ou selecionável */}
          {fixedClienteId ? (
            <div className="space-y-1.5">
              <Label>Cliente</Label>
              <p className="text-sm text-muted-foreground">{fixedClienteNome}</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label>Cliente (opcional)</Label>
              <ClienteCombobox value={clienteId} onChange={setClienteId} />
            </div>
          )}

          {hasEscritorios && !fixedEscritorioId && (
            <div className="space-y-1.5">
              <Label>Salvar em</Label>
              <select
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                value={escritorioId}
                onChange={(e) => setEscritorioId(e.target.value)}
              >
                <option value="">Pessoal</option>
                {escritorios?.map((esc) => (
                  <option key={esc.id} value={esc.id}>{esc.nome}</option>
                ))}
              </select>
            </div>
          )}
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancelar</Button>
          </DialogClose>
          <Button
            disabled={!uploadFile || uploading}
            onClick={handleUpload}
          >
            {uploading ? (
              <Spinner className="mr-2 text-inherit" />
            ) : (
              <Upload className="mr-2 h-4 w-4" />
            )}
            {uploading ? 'Enviando...' : 'Enviar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
